import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPaperAccount,
  getPaperOrders,
  getPaperPositions,
  submitPaperOrder,
} from "./paperTradingApi";
import "./AlpacaPaperTradingPanel.css";

const money = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(numeric);
};

const cleanSymbol = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9.-]/g, "")
    .slice(0, 15);

export function useAlpacaPaperTradingController() {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState("");
  const [symbol, setSymbol] = useState("AAPL");
  const [side, setSide] = useState("buy");
  const [quantity, setQuantity] = useState("1");
  const [orderType, setOrderType] = useState("market");
  const [timeInForce, setTimeInForce] = useState("day");
  const [limitPrice, setLimitPrice] = useState("");
  const [reviewOrder, setReviewOrder] = useState(null);
  const [submitState, setSubmitState] = useState({ status: "idle", message: "", order: null });

  const refreshPaperState = useCallback(async () => {
    setLoading(true);
    setConnectionError("");
    try {
      const [accountResponse, positionsResponse, ordersResponse] = await Promise.all([
        getPaperAccount(),
        getPaperPositions(),
        getPaperOrders("all", 25),
      ]);
      setAccount(accountResponse?.data || null);
      setPositions(Array.isArray(positionsResponse?.data) ? positionsResponse.data : []);
      setOrders(Array.isArray(ordersResponse?.data) ? ordersResponse.data : []);
    } catch (error) {
      setAccount(null);
      setPositions([]);
      setOrders([]);
      setConnectionError(error.message || "Unable to load the Alpaca paper account.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshPaperState();
  }, [refreshPaperState]);

  const accountMetrics = useMemo(
    () => [
      { label: "Paper Equity", value: money(account?.equity) },
      { label: "Paper Cash", value: money(account?.cash) },
      { label: "Buying Power", value: money(account?.buying_power) },
      { label: "Portfolio Value", value: money(account?.portfolio_value) },
    ],
    [account],
  );

  const prepareReview = (event) => {
    event.preventDefault();
    const normalizedSymbol = cleanSymbol(symbol);
    const normalizedQuantity = Number.parseInt(quantity, 10);
    const normalizedLimitPrice = Number(limitPrice);

    if (!normalizedSymbol) {
      setSubmitState({ status: "error", message: "Enter a valid stock symbol.", order: null });
      return;
    }
    if (!Number.isInteger(normalizedQuantity) || normalizedQuantity < 1) {
      setSubmitState({ status: "error", message: "Enter at least one whole share.", order: null });
      return;
    }
    if (orderType === "limit" && (!Number.isFinite(normalizedLimitPrice) || normalizedLimitPrice <= 0)) {
      setSubmitState({ status: "error", message: "Enter a positive limit price.", order: null });
      return;
    }

    const prepared = {
      symbol: normalizedSymbol,
      quantity: normalizedQuantity,
      side,
      order_type: orderType,
      time_in_force: timeInForce,
      ...(orderType === "limit" ? { limit_price: normalizedLimitPrice } : {}),
      client_order_id: `anina-paper-ui-${Date.now()}`,
    };
    setSymbol(normalizedSymbol);
    setReviewOrder(prepared);
    setSubmitState({
      status: "review",
      message: "Review carefully. The next confirmation sends this order to the Alpaca PAPER account only.",
      order: null,
    });
  };

  const cancelReview = () => {
    setReviewOrder(null);
    setSubmitState({ status: "idle", message: "", order: null });
  };

  const submitReviewedOrder = async () => {
    if (!reviewOrder || submitState.status === "submitting") return;
    setSubmitState({ status: "submitting", message: "Submitting to Alpaca PAPER…", order: null });
    try {
      const response = await submitPaperOrder(reviewOrder);
      const submittedOrder = response?.data || null;
      setSubmitState({
        status: "success",
        message: `Alpaca PAPER accepted the order${submittedOrder?.status ? ` with status ${submittedOrder.status}` : ""}.`,
        order: submittedOrder,
      });
      setReviewOrder(null);
      await refreshPaperState();
    } catch (error) {
      setSubmitState({
        status: "error",
        message: error.message || "The Alpaca paper order was not submitted.",
        order: null,
      });
    }
  };

  return {
    account,
    accountMetrics,
    connected: Boolean(account) && !connectionError,
    connectionError,
    limitPrice,
    loading,
    orderType,
    orders,
    positions,
    prepareReview,
    quantity,
    refreshPaperState,
    reviewOrder,
    setLimitPrice,
    setOrderType,
    setQuantity,
    setSide,
    setSymbol,
    setTimeInForce,
    side,
    submitReviewedOrder,
    cancelReview,
    submitState,
    symbol,
    timeInForce,
  };
}

export function AlpacaPaperAccountOverview({ controller }) {
  const {
    accountMetrics,
    connected,
    connectionError,
    loading,
    positions,
    refreshPaperState,
  } = controller;

  return (
    <section className="alpacaPaperPanel" aria-labelledby="alpaca-paper-title">
      <div className="alpacaPaperHeading">
        <div>
          <span className="alpacaPaperEyebrow">ALPACA PAPER · MANUAL EXECUTION V1</span>
          <h2 id="alpaca-paper-title">Real Paper Account</h2>
          <p>Orders here go only to Alpaca&apos;s paper environment. Live-money trading remains disabled.</p>
        </div>
        <div className={`alpacaPaperStatus ${connected ? "connected" : "offline"}`}>
          {loading ? "Checking…" : connected ? "PAPER connected" : "PAPER not connected"}
        </div>
      </div>

      <div className="alpacaPaperSafety" role="status">
        PAPER ONLY · no live brokerage endpoint · no browser-exposed Alpaca credentials · no AutoTrade execution
      </div>

      {connectionError && (
        <div className="alpacaPaperError" role="alert">
          <strong>Paper account unavailable.</strong>
          <span>{connectionError}</span>
          <button type="button" onClick={refreshPaperState}>Retry read-only connection</button>
        </div>
      )}

      {connected && (
        <>
          <div className="alpacaPaperMetrics">
            {accountMetrics.map((metric) => (
              <article key={metric.label}>
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
              </article>
            ))}
          </div>

          <section className="alpacaPaperPositions" aria-labelledby="alpaca-paper-positions-title">
            <div className="alpacaPaperSubheading">
              <div><span>ALPACA PAPER</span><h3 id="alpaca-paper-positions-title">Positions</h3></div>
              <small>{positions.length} shown</small>
            </div>
            <div className="alpacaPaperTableWrap">
              <table>
                <thead><tr><th>Symbol</th><th>Qty</th><th>Avg Entry</th><th>Market Value</th><th>Unrealized P/L</th></tr></thead>
                <tbody>
                  {positions.length === 0 ? (
                    <tr><td colSpan="5">No Alpaca paper positions.</td></tr>
                  ) : positions.map((position) => (
                    <tr key={position.asset_id || position.symbol}>
                      <th scope="row">{position.symbol}</th>
                      <td>{position.qty}</td>
                      <td>{money(position.avg_entry_price)}</td>
                      <td>{money(position.market_value)}</td>
                      <td>{money(position.unrealized_pl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </section>
  );
}

export function AlpacaPaperTicket({ controller }) {
  const {
    connected,
    limitPrice,
    orderType,
    prepareReview,
    quantity,
    reviewOrder,
    setLimitPrice,
    setOrderType,
    setQuantity,
    setSide,
    setSymbol,
    setTimeInForce,
    side,
    submitReviewedOrder,
    cancelReview,
    submitState,
    symbol,
    timeInForce,
  } = controller;

  return (
    <section className="alpacaPaperTicketCard" aria-labelledby="alpaca-paper-ticket-title">
      <form className="alpacaPaperTicket" onSubmit={prepareReview}>
        <div className="alpacaPaperSubheading">
          <div>
            <span>MANUAL PAPER ORDER</span>
            <h3 id="alpaca-paper-ticket-title">Alpaca PAPER Ticket</h3>
          </div>
          <small>Initial safety cap enforced server-side</small>
        </div>

        {!connected && (
          <p className="alpacaPaperEmpty">Connect the Alpaca paper account before reviewing a manual paper order.</p>
        )}

        <label>
          Symbol
          <input
            aria-label="Alpaca paper symbol"
            value={symbol}
            onChange={(event) => setSymbol(cleanSymbol(event.target.value))}
            maxLength="15"
            autoComplete="off"
            disabled={!connected}
          />
        </label>

        <fieldset className="alpacaPaperSideControl" disabled={!connected}>
          <legend>Side</legend>
          <button type="button" className={side === "buy" ? "active buy" : ""} onClick={() => setSide("buy")} aria-pressed={side === "buy"}>Buy</button>
          <button type="button" className={side === "sell" ? "active sell" : ""} onClick={() => setSide("sell")} aria-pressed={side === "sell"}>Sell</button>
        </fieldset>

        <label>
          Whole shares
          <input
            aria-label="Alpaca paper shares"
            type="number"
            min="1"
            step="1"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            disabled={!connected}
          />
        </label>

        <label>
          Order type
          <select aria-label="Alpaca paper order type" value={orderType} onChange={(event) => setOrderType(event.target.value)} disabled={!connected}>
            <option value="market">Market</option>
            <option value="limit">Limit</option>
          </select>
        </label>

        {orderType === "limit" && (
          <label>
            Limit price
            <input
              aria-label="Alpaca paper limit price"
              type="number"
              min="0.01"
              step="0.01"
              value={limitPrice}
              onChange={(event) => setLimitPrice(event.target.value)}
              disabled={!connected}
            />
          </label>
        )}

        <label>
          Time in force
          <select aria-label="Alpaca paper time in force" value={timeInForce} onChange={(event) => setTimeInForce(event.target.value)} disabled={!connected}>
            <option value="day">DAY</option>
            <option value="gtc">GTC</option>
          </select>
        </label>

        <p className="alpacaPaperTicketNote">
          Market orders are guarded server-side with current Alpaca market data before submission. The first Review click never submits an order.
        </p>

        <button className="alpacaPaperReviewButton" type="submit" disabled={!connected || submitState.status === "submitting"}>
          Review Alpaca PAPER Order
        </button>
      </form>

      {reviewOrder && (
        <div className="alpacaPaperInlineReview" role="region" aria-label="Alpaca paper order review">
          <div className="alpacaPaperInlineReviewHeading">
            <span>SECOND CONFIRMATION</span>
            <strong>Confirm this Alpaca PAPER order</strong>
          </div>
          <div className="alpacaPaperReviewCard">
            <div><span>Symbol</span><strong>{reviewOrder.symbol}</strong></div>
            <div><span>Side</span><strong>{reviewOrder.side.toUpperCase()}</strong></div>
            <div><span>Shares</span><strong>{reviewOrder.quantity}</strong></div>
            <div><span>Type</span><strong>{reviewOrder.order_type.toUpperCase()}</strong></div>
            <div><span>Time in force</span><strong>{reviewOrder.time_in_force.toUpperCase()}</strong></div>
            {reviewOrder.limit_price && <div><span>Limit</span><strong>{money(reviewOrder.limit_price)}</strong></div>}
            <p>{submitState.message}</p>
            <div className="alpacaPaperReviewActions">
              <button type="button" className="secondary" onClick={cancelReview}>Back</button>
              <button type="button" className="confirm" onClick={submitReviewedOrder} disabled={submitState.status === "submitting"}>
                {submitState.status === "submitting" ? "Submitting…" : "Submit to Alpaca PAPER"}
              </button>
            </div>
          </div>
        </div>
      )}

      {submitState.status === "success" && (
        <div className="alpacaPaperSuccess" role="status">
          <strong>{submitState.message}</strong>
          {submitState.order?.id && <span>Paper order ID: {submitState.order.id}</span>}
        </div>
      )}

      {submitState.status === "error" && (
        <div className="alpacaPaperError compact" role="alert">
          <span>{submitState.message}</span>
        </div>
      )}
    </section>
  );
}

function AlpacaPaperTradingPanel() {
  const controller = useAlpacaPaperTradingController();

  return (
    <div className="alpacaPaperStandalone">
      <AlpacaPaperAccountOverview controller={controller} />
      <AlpacaPaperTicket controller={controller} />
    </div>
  );
}

export default AlpacaPaperTradingPanel;
